import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { Addresses } from "types";
import { getAuthCookie } from "../../utils/authCookies";

const mapContainerStyle = {
    width: "100%",
    height: "400px",
};

const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

const SignageViewInstallation = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<Addresses[]>([]);

    const [installationOrder, setInstallationOrder] = useState(null);

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();
    const token = getAuthCookie("sessionToken");

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/${orderId}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await response.json();
                console.log("Installation Orders:", data);
                if (!response.ok) throw new Error(data.message || "Failed to fetch order details");

                setInstallationOrder(data);
            } catch (err: any) {
                console.error("Order Detail Fetch Error:", err.message);
            }
        };

        fetchOrderDetails();
    }, [orderId, token]);


    const formik = useFormik({
        initialValues: {
            installer: "",
            installation_date: "",
            billing_address: "",
            installation_address: "",
            isSameAsBilling: false,
            billingAddressId: "",
            installationAddressId: "",
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            image: null,
        },
        validationSchema: Yup.object({
            installer: Yup.string().required("Installer is required"),
            installation_date: Yup.string().required("Installation date is required"),
            billing_address: Yup.string().required("Billing address is required"),
            installation_address: Yup.string().required(
                "Installation address is required"
            ),
            image: Yup.mixed().required("Image is required"),
        }),
        onSubmit: async (values) => {
            setFormSubmitting(true);
            try {
                // Example file upload handling
                const imageUrl = values.image ? await uploadFile(values.image) : null;

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/categories`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            ...values,
                            image: imageUrl,
                        }),
                    }
                );

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || "Failed to create installation");
                }

                navigate("/agent/categories", {
                    state: { successMessage: "Installation added successfully!" },
                });
                toast.success("Installation added successfully!");
            } catch (error: any) {
                console.error(error);
                toast.error(`Error: ${error.message}`);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        },
    });

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "categories");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content">
            <div className="card">
                <div className="card-body">
                    <h2>Installation Request - View</h2>
                    <div className="row mb-3">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">
                                Company
                            </label>
                            <input type="text" value="Company 1" className="form-control" disabled />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">
                                Office
                            </label>
                            <input type="text" value="Office 1" className="form-control" disabled />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="form-label">
                                Agent
                            </label>
                            <input type="text" value="Agent 1" className="form-control" disabled />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label htmlFor="">Email</label>
                            <input type="text" name="agent_email" id="agent_email" className="form-control" value="john.doe@example.com" disabled />
                        </div>
                        <div className="col-md-4">
                            <label htmlFor="">Phone Number</label>
                            <input type="text" name="agent_phone" id="agent_phone" className="form-control" value="(123) 456-7890" disabled />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label htmlFor="">Billing Address</label>
                            <input type="text" name="agent_email" id="agent_email" className="form-control" value="456 Billing St, City, State" disabled />
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="">Installation Address</label>
                            <input type="text" name="agent_email" id="agent_email" className="form-control" value="456 Billing St, City, State" disabled />
                        </div>

                    </div>
                    <div className="row mb-3">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">
                                Scheduled Date
                            </label>
                            <input type="text" value="2025-04-20" className="form-control" disabled />
                            <p></p>
                        </div>
                        <div className="col-md-2 mb-3">
                            <label className="form-label">
                                Jobs Today
                            </label>
                            <input type="text" value="0" className="form-control" disabled />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">
                                Assigned To
                            </label>
                            <input type="text" value="Installer 1" className="form-control" disabled />
                        </div>
                        <div className="col-md-2 mb-3">
                            <label className="form-label">
                                Priority
                            </label>
                            <input type="text" value="High" className="form-control" disabled />
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-md-12">
                            <h4>Products</h4>
                            <div className="row mb-5">
                                <div className="col-md-12">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th className="text-center">SKU</th>
                                                <th className="text-center">Name</th>
                                                <th className="text-center">Qty </th>
                                                <th className="text-center">Source </th>
                                                <th className="text-center">Product Location Status</th>
                                                <th className="text-center">Update Status </th>
                                                <th className="text-center">Production Status </th>

                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="text-center">SP-123</td>
                                                <td className="text-center">Panel XYZ</td>
                                                <td className="text-center">1</td>
                                                <td className="text-center">Agent Inventory</td>
                                                <td className="text-center">
                                                    <span className="bg-warning p-1" style={{ borderRadius: "10px" }}>Pending Drop-off</span>
                                                </td>
                                                <td className="text-center">
                                                    <Select
                                                        id="update_status"
                                                        name="update_status"
                                                        options={[
                                                            { value: "with supplier", label: "With Supplier" },
                                                            { value: "in transit", label: "In Transit" },
                                                            { value: "on site", label: "On Site" },
                                                            { value: "declined", label: "Declined" },
                                                        ]}
                                                        value={{ value: "with supplier", label: "With Supplier" }}
                                                        onChange={(option) =>
                                                            formik.setFieldValue("company", option?.value || "")
                                                        }
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    <Select
                                                        id="product_status"
                                                        name="product_status"
                                                        options={[
                                                            { value: "not started", label: "Not Started" },
                                                            { value: "in production", label: "In Production" },
                                                            { value: "quality check", label: "Quality Check" },
                                                            { value: "ready for installation", label: "Ready for Installation" },
                                                        ]}
                                                        value={{ value: "in production", label: "In Production" }}
                                                        onChange={(option) =>
                                                            formik.setFieldValue("company", option?.value || "")
                                                        }
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </td>

                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-7">
                                <label htmlFor="">Instructions </label>
                                <textarea
                                    name="instructions"
                                    id="instructions"
                                    className="form-control"
                                    rows={4}
                                    placeholder="Please install by 9 AM sharp and notify the agent once complete." disabled
                                ></textarea>
                            </div>
                            <div className="col-md-5">
                                <table className="calculation-table">
                                    <tr>
                                        <th>Total Units</th>
                                        <td>2</td>
                                    </tr>
                                    <tr>
                                        <th>Discount</th>
                                        <td>
                                            <input type="text" name="discount" id="discount" value="$10.00" disabled />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Installation Charge</th>
                                        <td><input type="text" name="installation_charge" id="installation_charge" value="$40.00" disabled /></td>
                                    </tr>
                                    <tr>
                                        <th>Subtotal</th>
                                        <td>$50.00</td>
                                    </tr>
                                    <tr>
                                        <th>Tax</th>
                                        <td>$5.00</td>
                                    </tr>
                                    <tr>
                                        <th>Total</th>
                                        <td>$55.00</td>
                                    </tr>
                                </table>

                            </div>
                        </div>
                        {/* <div className="row mb-3">
                            <div className="col-md-6">
                                <label htmlFor="">Proof of Installation</label>
                                <div className="mb-3">
                                    <img
                                        src="https://picsum.photos/200/300" width={"100px"} className="me-2" />
                                    <img
                                        src="https://picsum.photos/200/300" width={"100px"} />
                                </div>
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignageViewInstallation;
